import { format, eachDayOfInterval, isWeekend, parseISO, addDays } from 'date-fns'
import type {
  PriceCalcInputs,
  PriceCalculationResult,
  DailyPriceBreakdown,
  WeatherCondition,
  PricingTier,
} from './price-calculator-types'
import {
  FACILITY_BASE_RATES,
  AIRCRAFT_SIZE_MULTIPLIER,
  WEATHER_MULTIPLIER,
} from './price-calculator-types'

// ── Deterministic pseudo-random from date string ────────────────────────────

function hashDate(dateStr: string): number {
  let hash = 0
  for (let i = 0; i < dateStr.length; i++) {
    const ch = dateStr.charCodeAt(i)
    hash = ((hash << 5) - hash + ch) | 0
  }
  return Math.abs(hash)
}

function seededRandom(seed: number): number {
  // Simple LCG
  const x = Math.sin(seed * 9301 + 49297) * 49297
  return x - Math.floor(x)
}

// ── Occupancy generation ────────────────────────────────────────────────────

function getOccupancy(dateStr: string, weekend: boolean): number {
  const h = hashDate(dateStr)
  const base = 45 + seededRandom(h) * 50 // 45-95 range
  const weekendBoost = weekend ? 8 + seededRandom(h + 1) * 7 : 0 // +8-15 on weekends
  return Math.min(98, Math.round(base + weekendBoost))
}

function occupancyMultiplier(pct: number): number {
  if (pct <= 50) return 1.0
  if (pct <= 70) return 1.0 + ((pct - 50) / 20) * 0.15 // 1.0 → 1.15
  if (pct <= 85) return 1.15 + ((pct - 70) / 15) * 0.15 // 1.15 → 1.3
  return 1.3 + ((Math.min(pct, 98) - 85) / 13) * 0.2 // 1.3 → 1.5
}

// ── Weather generation ──────────────────────────────────────────────────────

const WEATHER_POOL: WeatherCondition[] = [
  'clear', 'clear', 'clear', 'clear', 'clear', 'clear', // 60%
  'rain', 'rain', 'rain',                                // 15% (with rounding)
  'storm', 'storm',                                       // 10%
  'snow', 'snow',                                         // 10%
  'fog',                                                  // 5%
]

function getWeather(dateStr: string): WeatherCondition {
  const h = hashDate(dateStr + 'weather')
  return WEATHER_POOL[h % WEATHER_POOL.length]
}

// ── Holidays 2026 ───────────────────────────────────────────────────────────

interface HolidayEntry {
  date: string
  name: string
  major: boolean // major events get 1.5x, regular 1.35x
}

const HOLIDAYS_2026: HolidayEntry[] = [
  { date: '2026-01-01', name: "New Year's Day", major: true },
  { date: '2026-01-02', name: "New Year's Weekend", major: false },
  { date: '2026-02-08', name: 'Super Bowl LX', major: true },
  { date: '2026-02-09', name: 'Super Bowl Weekend', major: true },
  { date: '2026-02-16', name: "Presidents' Day", major: false },
  // Spring Break window
  { date: '2026-03-14', name: 'Spring Break', major: false },
  { date: '2026-03-15', name: 'Spring Break', major: false },
  { date: '2026-03-16', name: 'Spring Break', major: false },
  { date: '2026-03-17', name: 'Spring Break', major: false },
  { date: '2026-03-18', name: 'Spring Break', major: false },
  { date: '2026-03-19', name: 'Spring Break', major: false },
  { date: '2026-03-20', name: 'Spring Break', major: false },
  { date: '2026-03-21', name: 'Spring Break', major: false },
  { date: '2026-05-25', name: 'Memorial Day', major: false },
  { date: '2026-07-03', name: 'Independence Day Eve', major: true },
  { date: '2026-07-04', name: 'Independence Day', major: true },
  { date: '2026-07-05', name: 'Independence Day Weekend', major: true },
  // EAA AirVenture Oshkosh
  { date: '2026-07-27', name: 'EAA AirVenture', major: true },
  { date: '2026-07-28', name: 'EAA AirVenture', major: true },
  { date: '2026-07-29', name: 'EAA AirVenture', major: true },
  { date: '2026-07-30', name: 'EAA AirVenture', major: true },
  { date: '2026-07-31', name: 'EAA AirVenture', major: true },
  { date: '2026-08-01', name: 'EAA AirVenture', major: true },
  { date: '2026-08-02', name: 'EAA AirVenture', major: true },
  { date: '2026-09-07', name: 'Labor Day', major: false },
  // NBAA-BACE
  { date: '2026-10-19', name: 'NBAA-BACE', major: true },
  { date: '2026-10-20', name: 'NBAA-BACE', major: true },
  { date: '2026-10-21', name: 'NBAA-BACE', major: true },
  { date: '2026-11-26', name: 'Thanksgiving', major: true },
  { date: '2026-11-27', name: 'Thanksgiving Weekend', major: true },
  { date: '2026-12-24', name: 'Christmas Eve', major: true },
  { date: '2026-12-25', name: 'Christmas Day', major: true },
  { date: '2026-12-31', name: "New Year's Eve", major: true },
]

const HOLIDAY_MAP = new Map(HOLIDAYS_2026.map((h) => [h.date, h]))

// ── Tier classification ─────────────────────────────────────────────────────

function classifyTier(finalRate: number, baseRate: number): PricingTier {
  const ratio = finalRate / baseRate
  if (ratio <= 1.15) return 'low'
  if (ratio <= 1.45) return 'standard'
  return 'peak'
}

// ── Main calculation ────────────────────────────────────────────────────────

export function calculateMockPricing(inputs: PriceCalcInputs): PriceCalculationResult {
  const start = parseISO(inputs.startDate)
  const end = parseISO(inputs.endDate)

  // Validate: end must be after start, cap at 30 days
  if (end <= start) {
    return emptyResult()
  }

  const lastNight = addDays(end, -1)
  const dates = eachDayOfInterval({ start, end: lastNight })
  if (dates.length > 30) dates.length = 30

  const baseRate = FACILITY_BASE_RATES[inputs.facilityType]
  const aircraftMult = AIRCRAFT_SIZE_MULTIPLIER[inputs.aircraftSize]

  const days: DailyPriceBreakdown[] = dates.map((d) => {
    const dateStr = format(d, 'yyyy-MM-dd')
    const weekend = isWeekend(d)
    const occ = getOccupancy(dateStr, weekend)
    const weather = getWeather(dateStr)
    const holiday = HOLIDAY_MAP.get(dateStr)

    const occMult = occupancyMultiplier(occ)
    const weatherMult = WEATHER_MULTIPLIER[weather]
    const holidayMult = holiday ? (holiday.major ? 1.5 : 1.35) : 1.0

    const raw = baseRate * occMult * weatherMult * holidayMult * aircraftMult
    const finalRate = Math.round(raw)

    return {
      date: dateStr,
      dayLabel: format(d, 'EEE, MMM d'),
      occupancyPct: occ,
      weather,
      isHoliday: !!holiday,
      holidayName: holiday?.name,
      isWeekend: weekend,
      occupancyMultiplier: occMult,
      weatherMultiplier: weatherMult,
      holidayMultiplier: holidayMult,
      aircraftMultiplier: aircraftMult,
      baseRate,
      finalRate,
      pricingTier: classifyTier(finalRate, baseRate),
    }
  })

  const totalCost = days.reduce((sum, d) => sum + d.finalRate, 0)
  const rates = days.map((d) => d.finalRate)
  const peakRate = Math.max(...rates)
  const peakTotal = peakRate * days.length

  return {
    days,
    totalCost,
    averageNightlyRate: Math.round(totalCost / days.length),
    lowestRate: Math.min(...rates),
    highestRate: peakRate,
    nightCount: days.length,
    savingsVsPeak: peakTotal - totalCost,
    peakNights: days.filter((d) => d.pricingTier === 'peak').length,
    standardNights: days.filter((d) => d.pricingTier === 'standard').length,
    lowNights: days.filter((d) => d.pricingTier === 'low').length,
  }
}

function emptyResult(): PriceCalculationResult {
  return {
    days: [],
    totalCost: 0,
    averageNightlyRate: 0,
    lowestRate: 0,
    highestRate: 0,
    nightCount: 0,
    savingsVsPeak: 0,
    peakNights: 0,
    standardNights: 0,
    lowNights: 0,
  }
}

/** Default inputs for initial calculator state */
export function getDefaultInputs(): PriceCalcInputs {
  const start = addDays(new Date(), 3)
  const end = addDays(start, 7)
  return {
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd'),
    aircraftSize: 'midsize_jet',
    facilityType: 'hangar',
  }
}
